import Axios from 'axios'

import {
  StencilApiHeaders,
  XsrfResilient,
  areAllShipmentsAssigned,
} from '@/services/Helpers'

import {
  standardizeAddressForBigC,
  standardizeCart,
  standardizeOrder,
} from '@/services/Standardizers'

import { log } from '@/services/Log'

import { goToCart } from '@/services/FramedCheckout/navigationServices'

import {
  recoverShippingOptions,
} from '@/services/FramedCheckout/shipmentServices'

import {
  getCustomItems,
  getSubtotalPrice,
} from '@/helpers/checkoutHelpers'

import { EXTEND_SKU } from '@/data/constants'
import Listrak from '../Listrak'

export const getCartFromResponse = (rawCart, order) => {
  // Retrieving  product info from original order to avoid requests for item details
  log(`getCartFromResponse Start`, { rawCart, order })
  Listrak.submitCart(rawCart)
  let lineItems = rawCart?.lineItems?.physicalItems.map(({
    id, quantity, productId, variantId, ...rest
  }) => {
    const originalItem = (
      order?.cart?.lineItems.find((item) => item?.id === id)
      || order?.cart?.lineItems.find((item) => (
        item.productId === productId && item.variantId === variantId
      ))
    )
    // These props originally come from individual api requests in generate LineItemsFromCheckout
    const {
      options,
      url,
      originalPrice,
      leadTime,
      maxLeadTime,
      availabilityMessage,
      actualOverrideMessage,
      imageUrl,
      isFurniture,
      isReturnable,
      isDigital,
    } = originalItem
    const finalItem = {
      id,
      quantity,
      options,
      productId,
      variantId,
      ...rest,
      // Since the props won't change in the session then we don't need to request them again
      imageUrl,
      url,
      originalPrice,
      isReturnable,
      isDigital,
      subtotalPrice: getSubtotalPrice({ price: originalPrice, quantity }),
      leadTime,
      maxLeadTime,
      availabilityMessage,
      actualOverrideMessage,
      isFurniture,
    }
    return finalItem
  }).sort( // Sorting furniture to the end to match output from LineItemsFromCheckout
    (item1, item2) => (
      // eslint-disable-next-line no-nested-ternary
      (!item1.isFurniture && item2.isFurniture)
        ? -1
        : (item1.isFurniture && !item2.isFurniture)
          ? 1
          : 0
    ),
  )
  const customItems = getCustomItems({ lineItems, customItems: rawCart?.lineItems?.customItems })
  lineItems = [...lineItems, ...customItems]
  log(`getCartFromResponse End`, { lineItems })
  return standardizeCart({ ...rawCart, lineItems })
}

export const applyCoupon = XsrfResilient(async ({ couponCode, order }) => {
  log('applyCoupon', { couponCode, order })
  try {
    // Adds a Coupon Code to Checkout. - https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout-coupons/checkoutscouponsbycheckoutidpost
    const { data, status } = (await Axios.post(`/api/storefront/checkouts/${order?.cart?.id}/coupons`, { couponCode }))
    if (status === 200 && !!data) {
      const newCart = getCartFromResponse(data?.cart, order)
      log('applyCoupon -> recoverShippingOptions', { order, newCart })
      return await recoverShippingOptions({ order, newCart })
    }
  } catch {
    log('applyCoupon FAILED', { success: false })
    return { success: false }
  }
  log('applyCoupon FAILED', { success: false })
  return { success: false }
})

const getWarrantyId = ({ lineItems, id }) => {
  const sku = lineItems.find(item => item.id === id)?.sku;
  const warrantySku = `${EXTEND_SKU}${sku}`;
  const warrantyProduct = lineItems.find(item => item.sku.includes(warrantySku));
  return warrantyProduct?.id;
}

const updateQuantity = XsrfResilient(async ({ id, quantity }) => {
  const data = new FormData()
  const headers = StencilApiHeaders('post')
  data.append('items[0][id]', id)
  data.append('items[0][quantity]', quantity)
  // TODO: Migrate to v3
  return (await Axios.post('/remote/v1/cart/update', data, { headers }))
})

const removeCartItem = XsrfResilient(async ({ id, order }) => {
  log('removeCartItem', { id, order })
  const headers = StencilApiHeaders('delete')
  let success = true
  let newOrder
  const warrantyId = getWarrantyId({ lineItems: order?.cart?.lineItems, id })
  const isRemovingLastItemInCart = (
    order?.shipments.length === 1 &&
    (order?.shipments[0].lineItemIds.length === 1 ||
      (warrantyId && order?.shipments[0].lineItemIds.length === 2))
  )
  const shipment = order.shipments.find(({ lineItemIds }) => ~lineItemIds.indexOf(id))
  // Deletes a Line Item from the Cart. - https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout-cart-items/checkoutscartsitemsitemidbycheckoutidandcartiddelete
  try {
    if (warrantyId) {
      await Axios.delete(`/api/storefront/carts/${order?.cart?.id}/items/${warrantyId}`, { headers })
    }
    const response = (await Axios.delete(`/api/storefront/carts/${order?.cart?.id}/items/${id}`, { headers }))
    if (response.status === 204 && isRemovingLastItemInCart) {
      log('removeCartItem is removing the last item in the cart navigating user to cart page')
      await goToCart()
      return { success: true }
    }
    success = success && response.status === 200
    if (success) {
      if (shipment?.selectedShippingOption) {
        const data = {
          shippingAddress: standardizeAddressForBigC(shipment?.shippingAddress),
          lineItems: shipment?.lineItems
            .filter(({ id: itemId }) => itemId !== id)
            .map(({ id: itemId, quantity }) => ({
              itemId,
              quantity,
            })),
        }
        // check if this shipment will still exist
        if (data?.lineItems?.filter(({ quantity }) => quantity > 0).length) {
          try {
            const consignmentUpdateResponse = await Axios.put(
              `/api/storefront/checkouts/${order?.cart?.id}/consignments/${shipment?.id}?include=consignments.availableShippingOptions`,
              data,
              { headers: StencilApiHeaders('put') },
            )
            success = success && consignmentUpdateResponse?.status === 200
            const rawCart = consignmentUpdateResponse?.data?.cart
            const cart = getCartFromResponse(rawCart, order)
            newOrder = standardizeOrder(consignmentUpdateResponse?.data, cart)
            log('removeCartItem -> recoverShippingOptions', { id, order, newOrder })
            return await recoverShippingOptions({ order, newOrder })
          } catch {
            log('removeCartItem Consignment quantity update FAILED', { success: false })
            return { success: false }
          }
        }
      }
      const newCart = getCartFromResponse(response?.data, order)
      log('removeCartItem -> recoverShippingOptions', { id, order, newCart })
      if (!success) {
        log('removeCartItem FAILED', { success: false, newOrder })
        return { success: false, newOrder }
      }
      return await recoverShippingOptions({ order, newCart })
    }
  } catch (err) {
    log('removeCartItem FAILED', { success: false, url: `/api/storefront/carts/${order?.cart?.id}/items/${id}`, err })
  }
  log('removeCartItem FAILED', { success: false })
  return { success: false }
})

export const setCartItemQuantity = XsrfResilient(async ({
  shipmentId, id, quantity, order,
}) => {
  log('setCartItemQuantity', {
    shipmentId, id, quantity, order,
  })
  if (quantity === 0) {
    return removeCartItem({
      shipmentId, id, quantity, order,
    })
  }
  let success = true; let
    newOrder
  const product = order?.cart?.lineItems.find(({ id: pid }) => id === pid)
  const shipment = order.shipments.find(({ id }) => shipmentId === id)
  // Updates a Cart line item - https://developer.bigcommerce.com/api-reference/storefront/carts/cart-items/updatecartlineitem
  // Updates an existing, single line item quantity and the price of custom items in a cart.
  try {
    const warrantyId = getWarrantyId({ lineItems: order?.cart.lineItems, id })
    const response = await updateQuantity({ id, quantity })
    success = success && response?.status === 200
    if (success) { // Shipping option can be lost while interacting with the cart
      if (warrantyId) {
        const warrantyResponse = await updateQuantity({ id: warrantyId, quantity })
      }
      const { data } = await Axios.get('/api/storefront/cart?include=lineItems.physicalItems.options')
      const rawCart = Array.isArray(data) ? data[0] : data;
      const newCart = getCartFromResponse(rawCart, order)
      if (shipment?.selectedShippingOption) {
        const data = {
          shippingAddress: standardizeAddressForBigC(shipment?.shippingAddress),
          lineItems: shipment?.lineItems.map(({ id: itemId, quantity: originalQuantity }) => ({
            itemId,
            quantity: id === itemId || warrantyId === itemId ? quantity : originalQuantity,
          })),
        }
        const url = `/api/storefront/checkouts/${order?.cart?.id}/consignments/${shipment?.id}?include=consignments.availableShippingOptions`
        try {
          const consignmentUpdateResponse = await Axios.put(
            url,
            data,
            { headers: StencilApiHeaders('put') },
          )
          success = success && consignmentUpdateResponse?.status === 200
          const rawCart = consignmentUpdateResponse?.data?.cart
          const cart = getCartFromResponse(rawCart, order)
          newOrder = standardizeOrder(consignmentUpdateResponse?.data, cart)
          log('setCartItemQuantity => recoverShippingOptions', { order, newOrder, areAllShipmentsAssigned: areAllShipmentsAssigned(newOrder) })
          return await recoverShippingOptions({ order, newOrder })
        } catch {
          log('setCartItemQuantity Consignment quantity update FAILED', {
            success: false,
            url,
            data,
            options: { headers: StencilApiHeaders('put') },
          })
          return { success: false }
        }
      }
      log('setCartItemQuantity => recoverShippingOptions', {
        order, newCart,
      })
      return await recoverShippingOptions({ order, newCart })
    }
    return { success: false }
  } catch {
    return { success: false }
  }
})
